import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { Table } from 'antd';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import VerticalDate from '@components/Dates/VerticalDate';
import CustomSwitch from '@components/Gui/CustomSwitch';
import EmptyState from '@components/Gui/EmptyState';
import Money from '@components/Gui/Money';
import { NwButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import PrintableLayout from '@components/Printable/PrintableLayout';
import PrintableLayoutOptionsSelector, {
    DEFAULT_PRINT_LAYOUT_OPTION,
} from '@components/Printable/PrintableLayoutOptionsSelector';
import ProjectTitle from '@components/Project/ProjectTitle';
import { adaptReceivableStatements } from '@components/StatementRowsCommon/adapters';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import { useStatementContext } from '../StatementContext';
import { PrintableDrawerWrapper, StatementTableContainer } from '../StatementUI';
import { isMainFee } from './ReceivableHelpers';
import { ClientNameInTitle } from './ReceivableStyles';
import ReceivableTotalColumn from './ReceivableTotalColumn';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * ReceivablePrintable
 *
 * Printable version of the receivable statement body.
 * For client statements.
 */
const ReceivablePrintable = ({ balances, transactions, onClose, statementTotals }) => {
    const { contactID, contactType, showSubjectNameInTitle } = useStatementContext();

    const componentRef = useRef();
    const [showJobDetails, setShowJobDetails] = useState(true);
    // const [showTotalBalances, setShowTotalBalances] = useState(false);
    // const [showAvailableBalances, setShowAvailableBalances] = useState(true);
    const [showTotalLines, setShowTotalLines] = useState(true);
    const [layoutOption, setLayoutOption] = useState(DEFAULT_PRINT_LAYOUT_OPTION);
    const updateLayoutOption = useCallback((patch) => {
        setLayoutOption((prev) => ({ ...prev, ...patch }));
    }, []);

    const transactionsToDisplay = useMemo(() => {
        if (transactions && transactions.length > 0) {
            return adaptReceivableStatements(transactions);
        }
        return [];
    }, [transactions]);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        bodyClass: 'print-body',
        documentTitle: 'Statement',
        onAfterPrint: () => {
            onClose();
        },
    });

    const isStatementPaid = (statement) => {
        return statement.PaidStatus === 'Paid';
    };

    const currentColumnNumber = useMemo(() => {
        let count = 3 + Number(showJobDetails);
        if (contactType !== 'agency') {
            count += 1; // Job Amount column
        }
        return count;
    }, [showJobDetails, contactType]);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'Date',
            key: 'Date',
            render: (text, record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="date-container">
                            <VerticalDate
                                size="small"
                                cancelled={record.Transaction.WorkDateCancelled}
                                date={record.Transaction.RefDate}
                                verticalPadding="0"
                            />
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: 'Client',
            dataIndex: 'ClientInvoiceStatus',
            key: 'ClientInvoiceStatus',
            render: (text, record) => {
                if (isStatementPaid(record)) {
                    return <small className="payment-status">PAID</small>;
                }
                if (record.InvoiceID) {
                    return <small className="payment-status">INVOICED</small>;
                }
                return <small className="payment-status">NOT INVOICED</small>;
            },
        },
        {
            title: 'Title',
            key: 'Title',
            width: showJobDetails ? '40%' : '60%',
            render: (record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="transaction-details">
                            {showSubjectNameInTitle && record.ClientName && (
                                <ClientNameInTitle>{record.ClientName}</ClientNameInTitle>
                            )}
                            {record.Transaction.ModelID && <p>{getContactName(record.Transaction.ModelID)}</p>}
                            <TransactionTitle transaction={record.Transaction} />
                        </div>
                    );
                } else {
                    return (
                        <div className="transaction-details transaction-commission-name">
                            <small>{record.isClientAdvance ? 'client advance / payroll' : 'agency fee'}</small>
                        </div>
                    );
                }
            },
        },
    ];

    if (showJobDetails) {
        columns.push({
            title: 'Project',
            dataIndex: 'Project',
            key: 'Project',
            width: '35%',
            className: 'top-aligned',
            render: (text, record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="project-details">
                            {record.Transaction.ServiceID && (
                                <p className="transaction-client">{getContactName(record.Transaction.ServiceID)}</p>
                            )}
                            {record.Transaction.Project && (
                                <ProjectTitle project={record.Transaction.Project} compact hideAlert />
                            )}
                            <small>{record.Transaction.Description}</small>
                        </div>
                    );
                } else {
                    if (record.TransactionPerc) {
                        return <p className="commission-perc">{record.TransactionPerc}%</p>;
                    } else {
                        return <></>;
                    }
                }
            },
        });
    }

    // Add Job Amount column only if contactType is not 'agency'
    if (contactType !== 'agency') {
        columns.push({
            title: 'Job Amount',
            dataIndex: 'Numbers',
            key: 'Numbers',
            align: 'right',
            width: '10%',
            render: (text, record) => {
                if (isMainFee(record) && record.Transaction.Family === 'JobFee') {
                    return (
                        <div className="transaction-gross">
                            {(record.Transaction.Quantity !== 1 ||
                                !['Flat', 'Units'].includes(record.Transaction.TypeQuantity)) && (
                                <p>
                                    <Money
                                        amount={record.Transaction.SingleFee}
                                        currency={record.Transaction.Currency}
                                    />
                                    &nbsp;{`x ${record.Transaction.Quantity} ${record.Transaction.TypeQuantity}`}
                                </p>
                            )}
                            <p>
                                <Money
                                    highlightCurrency
                                    amount={record.Transaction.TotalFee}
                                    currency={record.Transaction.Currency}
                                />
                            </p>
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        });
    }

    columns.push({
        title: 'Amount',
        key: 'Amount',
        width: '20%',
        align: 'right',
        render: (record) => {
            return (
                <div className="statement-total">
                    <ReceivableTotalColumn statement={record} transaction={record.Transaction} isExpanded printable />
                </div>
            );
        },
    });

    const getRowClassName = (record) => {
        let cn = 'statement-row';
        if (isStatementPaid(record)) {
            cn += ' statement-row-paid';
        }
        if (record.isCommission) {
            cn += ' commission-row';
        }
        if (record.isClientAdvance) {
            cn += ' advance-row';
        }
        if (record.Transaction.WorkDateCancelled) {
            cn += ' cancelled';
        }
        return cn;
    };

    return (
        <PrintableDrawerWrapper>
            <div className="printable-statement-header">
                <div className="printable-statement-header-left">
                    <PrintableLayoutOptionsSelector
                        layoutOption={layoutOption}
                        onLayoutOptionChange={updateLayoutOption}
                    />
                    <NwDivider dark>Statement Details Options</NwDivider>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showTotalLines}
                            text="show lines total at the bottom"
                            onChange={setShowTotalLines}
                        />
                        <CustomSwitch
                            size="small"
                            value={showJobDetails}
                            text="show job/project details"
                            onChange={setShowJobDetails}
                        />
                    </div>
                </div>
                <div className="printable-statement-header-right">
                    <NwButton
                        size="big"
                        primary
                        label="Print"
                        icon={byPrefixAndName.fasl['print']}
                        onClick={handlePrint}
                    />
                </div>
            </div>
            <div className="printable-content-wrapper">
                <StatementTableContainer ref={componentRef}>
                    <PrintableLayout
                        showCompanyHeader={layoutOption.showCompanyHeader}
                        showCompanyFooter={layoutOption.showCompanyFooter}
                        onlyLogoInHeader={layoutOption.onlyLogoInHeader}
                    >
                        <div className="printable-top">
                            <div className="printable-description">
                                <small>Statement for: </small>
                                <h2>{getContactName(contactID)}</h2>
                            </div>
                        </div>
                        {transactionsToDisplay.length > 0 ? (
                            <Table
                                className="statement-table statement-table-printable"
                                columns={columns}
                                dataSource={transactionsToDisplay}
                                pagination={false}
                                rowClassName={(record) => getRowClassName(record)}
                                summary={(pageData) => {
                                    if (!showTotalLines) {
                                        return null;
                                    }
                                    return (
                                        <>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell
                                                    colSpan={currentColumnNumber - 1}
                                                ></Table.Summary.Cell>
                                                <Table.Summary.Cell>
                                                    <strong>Total</strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell>
                                                    {statementTotals.map((item) => (
                                                        <Money
                                                            key={item.Currency}
                                                            style={{ display: 'block', textAlign: 'right' }}
                                                            dineroObj={item.Dinero}
                                                            redIfNegative
                                                            boldIfDefaultCurrency
                                                        />
                                                    ))}
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </>
                                    );
                                }}
                            />
                        ) : (
                            <EmptyState message="No Results" />
                        )}
                    </PrintableLayout>
                </StatementTableContainer>
            </div>
        </PrintableDrawerWrapper>
    );
};

export default ReceivablePrintable;
